import React, { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import Cookies from "universal-cookie";
import TagManager from "react-gtm-module";

import { Amplify, API, Auth } from "aws-amplify";

import themeWrapper from "./Components/Themes/themeWrapper";
import handleCustomEvent from "./Components/Helpers/CustomEventsHandler";

import Home from "./Pages/Home";
import Sales from "./Pages/Sales";
import Redirecting from "./Pages/Redirecting";
import PaymentMethods from "./Pages/PaymentMethods";

export const ThemeContext = React.createContext();
export const ThemeConstants = React.createContext();

export const AuthContext = React.createContext();

function App() {
  // Theme Initiation
  const currentTheme = process.env.REACT_APP_THEME;
  let themeConfig, themeConstants;

  const cookies = new Cookies();

  switch (currentTheme) {
    case "helmsmarthome":
      themeConfig = themeWrapper().helmsmarthomeTheme;
      themeConstants = themeWrapper().helmConstants;
      break;
    case "homelink":
      themeConfig = themeWrapper().homelinkTheme;
      themeConstants = themeWrapper().homelinkConstants;
      break;
    default:
      break;
  }

  useEffect(() => {
    if (currentTheme === "homelink") {
      document.body.classList.add("bg-[#1A1F26]");
    }
  }, [currentTheme]);

  useEffect(() => {
    const tagManagerArgs = {
      gtmId: process.env.REACT_APP_GTM_ID,
    };
    TagManager.initialize(tagManagerArgs);
  }, []);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  Amplify.configure({
    API: {
      endpoints: [
        {
          name: "Customer",
          endpoint: themeConstants.ACCOUNT_API_URL,
        },
      ],
    },
    Auth: {
      mandatorySignIn: true,
      region: themeConstants.COGNITO_REGION,
      userPoolId: themeConstants.COGNITO_USER_POOL_ID,
      userPoolWebClientId: themeConstants.COGNITO_APP_CLIENT_ID,
      // COOKIE Config
      cookieStorage: {
        domain: themeConstants.COOKIE_STORAGE_DOMAIN,
        path: themeConstants.COOKIE_STORAGE_PATH,
        expires: themeConstants.COOKIE_STORAGE_EXPIRES,
        secure: themeConstants.COOKIE_STORAGE_SECURE,
      },
      // Hosted UI configuration
      oauth: {
        domain: themeConstants.COGNITO_DOMAIN,
        scope: ["phone", "email", "profile", "openid"],
        redirectSignIn: themeConstants.COGNITO_REDIRECTSIGNIN,
        redirectSignOut: themeConstants.COGNITO_REDIRECTSIGNOUT,
        responseType: "code", // or 'token', note that REFRESH token will only be generated when the responseType is code
      },
    },
  });

  Auth.configure();
  API.configure();

  const sessionInit = async () => {
    try {
      const session = await Auth.currentSession();
      if (session) setAuthStatus(true);
      const currentUser = await Auth.currentAuthenticatedUser();
      if (currentUser) setUser(currentUser);
      // Sends GA4 event : login
      const loginEvent = {
        event: "login",
        method: "Cognito",
      };
      handleCustomEvent(loginEvent);
    } catch (error) {
      cookies.remove("amplify-redirected-from-hosted-ui", {
        domain: themeConstants.COOKIE_STORAGE_DOMAIN,
        path: "/",
      });
      console.log(error);
    }
  };

  useEffect(() => {
    sessionInit();
  }, []);

  const setAuthStatus = (authenticated) => {
    setIsAuthenticated(authenticated);
  };

  const setCurrentUser = (user) => {
    setUser(user);
  };

  const authProps = {
    isAuthenticated,
    user,
    setAuthStatus,
    setCurrentUser,
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ThemeContext.Provider value={themeConfig}>
            <ThemeConstants.Provider value={themeConstants}>
              <AuthContext.Provider value={authProps}>
                <Home />
              </AuthContext.Provider>
            </ThemeConstants.Provider>
          </ThemeContext.Provider>
        }
      />
      <Route
        path="/sales"
        element={
          <ThemeContext.Provider value={themeConfig}>
            <ThemeConstants.Provider value={themeConstants}>
              <AuthContext.Provider value={authProps}>
                <Sales />
              </AuthContext.Provider>
            </ThemeConstants.Provider>
          </ThemeContext.Provider>
        }
      />
      <Route
        path="/payment-methods"
        element={
          <ThemeContext.Provider value={themeConfig}>
            <ThemeConstants.Provider value={themeConstants}>
              <AuthContext.Provider value={authProps}>
                <PaymentMethods />
              </AuthContext.Provider>
            </ThemeConstants.Provider>
          </ThemeContext.Provider>
        }
      />
      <Route
        path="/redirecting"
        element={
          <ThemeContext.Provider value={themeConfig}>
            <ThemeConstants.Provider value={themeConstants}>
              <AuthContext.Provider value={authProps}>
                <Redirecting />
              </AuthContext.Provider>
            </ThemeConstants.Provider>
          </ThemeContext.Provider>
        }
      />
    </Routes>
  );
}

export default App;
